import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, XCircle } from "lucide-react";
import {
 Card,
 CardHeader,
 CardTitle,
 CardContent,
 Toast,
 ToastType,
 ModernButton,
 Button,
 ConfirmDialog,
 ProposalEditPageSkeleton,
} from "../../components/ui";
import { ProposalForm } from "../../components/proposals";
import { ProposalFormData } from "../../types";
import { useProposals } from "../../hooks/useProposals";
import { apiService } from "../../services/api";

export const ProposalEditPage: React.FC = () => {
 const navigate = useNavigate();
 const { id } = useParams<{ id: string }>();
 const { updateProposal, approveProposal, rejectProposal } = useProposals();

 const [proposal, setProposal] = useState<any>(null);
 const [isLoading, setIsLoading] = useState(true);
 const [isSubmitting, setIsSubmitting] = useState(false);
 const [toast, setToast] = useState<{
 message: string;
 type: ToastType;
 } | null>(null);
 const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
 const [actionType, setActionType] = useState<"accept" | "reject" | null>(
 null
 );

 useEffect(() => {
 if (id) {
 fetchProposal(id);
 }
 }, [id]);

 const fetchProposal = async (proposalId: string) => {
 try {
 setIsLoading(true);
 const response = await apiService.getProposalById(proposalId);
 setProposal(response);
 } catch (err) {
 console.error("Erro ao buscar proposta:", err);
 setToast({
 message: "Erro ao carregar proposta. Tente novamente.",
 type: "error",
 });
 } finally {
 setIsLoading(false);
 }
 };

 const handleSubmit = async (data: ProposalFormData) => {
 if (!id) return;

 setIsSubmitting(true);
 try {
 await updateProposal(id, data);
 setToast({
 message: "Proposta atualizada com sucesso!",
 type: "success",
 });
 // Recarregar a proposta para atualizar os dados na tela
 await fetchProposal(id);
 } catch (err) {
 console.error("Erro ao atualizar proposta:", err);
 setToast({
 message: "Erro ao atualizar proposta. Tente novamente.",
 type: "error",
 });
 } finally {
 setIsSubmitting(false);
 }
 };

 const handleCancel = () => {
 navigate("/proposals");
 };

 const handleAccept = () => {
 setActionType("accept");
 setConfirmDialogOpen(true);
 };

 const handleReject = () => {
 setActionType("reject");
 setConfirmDialogOpen(true);
 };

 const confirmAction = async () => {
 if (!id || !actionType) return;

 setIsSubmitting(true);
 try {
 if (actionType === "accept") {
 await approveProposal(id);
 } else {
 await rejectProposal(id);
 }
 setToast({
 message: `Proposta ${
 actionType === "accept" ? "aceita" : "rejeitada"
 } com sucesso!`,
 type: "success",
 });

 // Redirecionar para a lista de propostas após 1 segundo
 setTimeout(() => {
 navigate("/proposals");
 }, 1000);
 } catch (err) {
 console.error(
 `Erro ao ${actionType === "accept" ? "aceitar" : "rejeitar"} proposta:`,
 err
 );
 setToast({
 message: `Erro ao ${
 actionType === "accept" ? "aceitar" : "rejeitar"
 } proposta. Tente novamente.`,
 type: "error",
 });
 } finally {
 setIsSubmitting(false);
 setConfirmDialogOpen(false);
 setActionType(null);
 }
 };

 const canEditProposal = (status: string) => {
 const nonEditableStatuses = ["accepted", "approved", "rejected", "expired"];
 return !nonEditableStatuses.includes(status);
 };

 if (isLoading) {
 return <ProposalEditPageSkeleton />;
 }

 if (!proposal) {
 return (
 <div className="space-y-6">
 <div className="flex items-center gap-4">
 <Button
 variant="outline"
 onClick={() => navigate("/proposals")}
 >
 <ArrowLeft className="h-4 w-4 mr-2" />
 Voltar
 </Button>
 <h1 className="text-2xl font-bold text-foreground">
 Editar Proposta
 </h1>
 </div>
 <Card>
 <CardContent className="py-12">
 <p className="text-center text-muted-foreground">
 Proposta não encontrada
 </p>
 </CardContent>
 </Card>
 </div>
 );
 }

 const isEditable = canEditProposal(proposal.status);

 return (
 <div className="space-y-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-4">
 <Button
 variant="outline"
 onClick={() => navigate("/proposals")}
 className="flex items-center gap-2"
 >
 <ArrowLeft className="h-4 w-4" />
 Voltar
 </Button>
 <div>
 <h1 className="text-2xl font-bold text-foreground">
 Editar Proposta #{proposal.id?.substring(0, 8) || ""}
 </h1>
 <p className="mt-1 text-sm text-muted-foreground">
 Atualize os dados da proposta de negociação
 </p>
 </div>
 </div>

 {/* Botões de Ação */}
 <div className="flex items-center gap-3">
 <Button
 variant="outline"
 onClick={handleReject}
 disabled={isSubmitting || !isEditable}
 className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:border-red-300"
 >
 <XCircle className="h-4 w-4" />
 Recusar Proposta
 </Button>
 <Button
 onClick={handleAccept}
 disabled={isSubmitting || !isEditable}
 className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
 >
 <CheckCircle className="h-4 w-4" />
 Aceitar Proposta
 </Button>
 </div>
 </div>

 {/* Form */}
 <Card>
 <CardHeader>
 <CardTitle>Dados da Proposta</CardTitle>
 </CardHeader>
 <CardContent>
 <ProposalForm
 proposal={proposal}
 onSubmit={handleSubmit}
 onCancel={handleCancel}
 isSubmitting={isSubmitting}
 />
 </CardContent>
 </Card>

 {/* Toast Notifications */}
 {toast && (
 <Toast
 message={toast.message}
 type={toast.type}
 onClose={() => setToast(null)}
 duration={3000}
 />
 )}

 {/* Confirm Dialog */}
 <ConfirmDialog
 isOpen={confirmDialogOpen}
 title={
 actionType === "accept" ? "Aceitar Proposta" : "Recusar Proposta"
 }
 description={
 actionType === "accept"
 ? "Tem certeza que deseja aceitar esta proposta? Esta ação não pode ser desfeita."
 : "Tem certeza que deseja recusar esta proposta? Esta ação não pode ser desfeita."
 }
 onConfirm={confirmAction}
 onClose={() => {
 setConfirmDialogOpen(false);
 setActionType(null);
 }}
 type={actionType === "accept" ? "success" : "danger"}
 confirmText={actionType === "accept" ? "Sim, Aceitar" : "Sim, Recusar"}
 isLoading={isSubmitting}
 />
 </div>
 );
};
